import { AlertTriangle, BookOpen, FileCode2, LayoutDashboard, ListChecks, LogOut, Share2, ShieldCheck, Smartphone, Users, UserCog } from "lucide-react";
import { NavLink, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";
import { LanguageSwitcher } from "./LanguageSwitcher";

// adminOnly:true masque l'entrée pour les rôles non-admin.
// labelKey est résolu via i18next avec t(`sidebar.${labelKey}`).
const NAV: Array<{
  to: string;
  labelKey: string;
  icon: typeof LayoutDashboard;
  end: boolean;
  adminOnly: boolean;
}> = [
  { to: "/",              labelKey: "dashboard",     icon: LayoutDashboard, end: true,  adminOnly: false },
  { to: "/phones",        labelKey: "phones",        icon: Smartphone,      end: false, adminOnly: false },
  { to: "/scripts",       labelKey: "scripts",       icon: FileCode2,       end: false, adminOnly: true  },
  { to: "/jobs",          labelKey: "jobs",          icon: ListChecks,      end: false, adminOnly: false },
  { to: "/accounts",      labelKey: "accounts",      icon: Users,           end: false, adminOnly: false },
  { to: "/failures",      labelKey: "failures",      icon: AlertTriangle,   end: false, adminOnly: false },
  { to: "/share",         labelKey: "shareCenter",   icon: Share2,          end: false, adminOnly: false },
  { to: "/users",         labelKey: "users",         icon: UserCog,         end: false, adminOnly: true  },
  { to: "/documentation", labelKey: "documentation", icon: BookOpen,        end: false, adminOnly: false },
];

export function Layout(): JSX.Element {
  const { t } = useTranslation();
  const { user, logout, isAdmin } = useAuth();
  const visibleNav = NAV.filter((n) => !n.adminOnly || isAdmin);

  return (
    <div className="min-h-screen bg-ink-900 flex">
      <aside className="w-64 shrink-0 bg-ink-800/60 border-r border-ink-700 flex flex-col">
        <div className="h-16 flex items-center px-6 border-b border-ink-700">
          <span className="text-lg font-bold bg-gradient-to-r from-velvet-400 to-velvet-600 bg-clip-text text-transparent">
            Velvet Hub
          </span>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {visibleNav.map(({ to, labelKey, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                [
                  "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                  isActive
                    ? "bg-velvet-600/15 text-velvet-300 border border-velvet-600/30"
                    : "text-ink-500 hover:text-white hover:bg-ink-700/50 border border-transparent",
                ].join(" ")
              }
            >
              <Icon className="size-4" />
              {t(`sidebar.${labelKey}`)}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-ink-700">
          <LanguageSwitcher />
          <div className="p-3 border-t border-ink-700/50">
            <div className="px-3 py-2 text-xs text-ink-500 truncate flex items-center gap-2">
              {isAdmin && <ShieldCheck className="size-3.5 text-velvet-300 shrink-0" />}
              <span className="truncate">{user?.email}</span>
            </div>
            <button
              onClick={() => void logout()}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-ink-500 hover:text-white hover:bg-ink-700/50 transition-colors"
            >
              <LogOut className="size-4" />
              {t("sidebar.logout")}
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
